import React, { useState, useRef } from 'react';
import { RefreshCw, Database, Filter, Search, DollarSign, Play, CheckSquare, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { useCTraderManagement } from '@/hooks/use-ctrader-management';
import { useCTraderPagination } from '@/hooks/use-ctrader-pagination';
import { CTraderConnection } from '@/services/ctrader-token-refresh-service';
import { batchProcessingService } from '@/services/batch-processing-service';
import { EnhancedBatchProgressDialog } from './EnhancedBatchProgressDialog';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';

interface CTraderTokenManagementProps {}

export function CTraderTokenManagement({}: CTraderTokenManagementProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedConnections, setSelectedConnections] = useState<Set<string>>(new Set());
  const [batchJobId, setBatchJobId] = useState<string | null>(null);
  const [batchJobType, setBatchJobType] = useState<'token_refresh' | 'balance_sync'>('token_refresh');
  const [showBatchProgress, setShowBatchProgress] = useState(false);
  
  // Track previous filters to detect changes without causing re-renders
  const previousFiltersRef = useRef({ searchQuery: '', statusFilter: 'all' });
  
  const { toast } = useToast();
  
  const {
    connections,
    isLoading,
    isRefreshing,
    isSyncingBalances,
    refreshingConnections,
    syncingBalanceConnections,
    refreshSingleToken,
    syncSingleBalance
  } = useCTraderManagement();

  const {
    paginatedConnections,
    paginationConfig,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    resetPagination
  } = useCTraderPagination(connections, searchQuery, statusFilter, 50);

  // Handle filter changes and reset pagination without causing infinite loops
  React.useEffect(() => {
    const currentFilters = { searchQuery, statusFilter };
    const previousFilters = previousFiltersRef.current;
    
    // Only reset if filters actually changed
    if (currentFilters.searchQuery !== previousFilters.searchQuery || 
        currentFilters.statusFilter !== previousFilters.statusFilter) {
      
      console.log('Filters changed, resetting pagination and selection:', currentFilters);
      resetPagination();
      setSelectedConnections(new Set());
      previousFiltersRef.current = currentFilters;
    }
  }, [searchQuery, statusFilter, resetPagination]);

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'connected': return 'default';
      case 'disconnected': return 'secondary';
      case 'failed': return 'destructive';
      default: return 'outline';
    }
  };

  const getRefreshStatusBadgeVariant = (status: string | null) => {
    switch (status) {
      case 'success': return 'default';
      case 'failed': return 'destructive';
      case 'pending': return 'secondary';
      default: return 'outline';
    }
  };

  const getBalanceSyncStatusBadgeVariant = (status: string | null) => {
    switch (status) {
      case 'success': return 'default';
      case 'failed': return 'destructive';
      case 'pending': return 'secondary';
      default: return 'outline';
    }
  };

  const isTokenExpired = (expireDate: string | null) => {
    if (!expireDate) return false;
    return new Date(expireDate) <= new Date();
  };

  const handleSelectConnection = React.useCallback((connectionId: string, checked: boolean | "indeterminate") => {
    console.log('handleSelectConnection called:', { connectionId, checked });
    
    // Handle indeterminate state as false
    const isChecked = checked === true;
    
    setSelectedConnections(prevSelected => {
      const newSelection = new Set(prevSelected);
      console.log('Previous selection:', Array.from(prevSelected));
      
      if (isChecked) {
        newSelection.add(connectionId);
        console.log('Added connection:', connectionId);
      } else {
        newSelection.delete(connectionId);
        console.log('Removed connection:', connectionId);
      }
      
      console.log('New selection:', Array.from(newSelection));
      return newSelection;
    });
  }, []);

  const handleSelectAll = React.useCallback((checked: boolean | "indeterminate") => {
    console.log('handleSelectAll called:', { checked, connectionsCount: paginatedConnections.length });
    
    // Handle indeterminate state as false
    const isChecked = checked === true;
    
    setSelectedConnections(prevSelected => {
      if (isChecked) {
        // Select all connections on current page
        const newSelection = new Set(prevSelected);
        paginatedConnections.forEach(connection => {
          newSelection.add(connection.id);
        });
        console.log('Selected all connections on page:', Array.from(newSelection));
        return newSelection;
      } else {
        // Deselect all connections on current page
        const newSelection = new Set(prevSelected);
        paginatedConnections.forEach(connection => {
          newSelection.delete(connection.id);
        });
        console.log('Deselected all connections on page:', Array.from(newSelection));
        return newSelection;
      }
    });
  }, [paginatedConnections]);

  const handleBatchRefresh = async () => {
    if (selectedConnections.size === 0) {
      toast({
        title: "No connections selected",
        description: "Please select at least one connection to refresh",
        variant: "destructive"
      });
      return;
    }

    try {
      const jobId = await batchProcessingService.startBatchTokenRefresh(
        Array.from(selectedConnections)
      );
      
      setBatchJobId(jobId);
      setBatchJobType('token_refresh');
      setShowBatchProgress(true);
      
      toast({
        title: "Batch token refresh started",
        description: `Processing ${selectedConnections.size} tokens with detailed progress tracking`,
      });
      
      setSelectedConnections(new Set());
    } catch (error) {
      toast({
        title: "Failed to start batch refresh",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive"
      });
    }
  };

  const handleBatchBalanceSync = async () => {
    if (selectedConnections.size === 0) {
      toast({
        title: "No connections selected",
        description: "Please select at least one connection to sync",
        variant: "destructive"
      });
      return;
    }

    try {
      const jobId = await batchProcessingService.startBatchBalanceSync(
        Array.from(selectedConnections)
      );
      
      setBatchJobId(jobId);
      setBatchJobType('balance_sync');
      setShowBatchProgress(true);
      
      toast({
        title: "Batch balance sync started",
        description: `Processing ${selectedConnections.size} accounts with detailed progress tracking`,
      });
      
      setSelectedConnections(new Set());
    } catch (error) {
      toast({
        title: "Failed to start batch balance sync",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive"
      });
    }
  };

  // Memoize calculated values to prevent unnecessary re-renders
  const stats = React.useMemo(() => ({
    total: connections.length,
    connected: connections.filter(c => c.status === 'connected').length,
    expired: connections.filter(c => isTokenExpired(c.expire_date)).length,
    failed: connections.filter(c => c.refresh_status === 'failed').length,
    balanceSyncFailed: connections.filter(c => c.balance_sync_status === 'failed').length
  }), [connections]);

  // Calculate if all connections on current page are selected
  const isAllSelected = React.useMemo(() => {
    if (paginatedConnections.length === 0) return false;
    return paginatedConnections.every(c => selectedConnections.has(c.id));
  }, [paginatedConnections, selectedConnections]);

  // Calculate if some (but not all) connections are selected
  const isSomeSelected = React.useMemo(() => {
    return paginatedConnections.some(c => selectedConnections.has(c.id)) && !isAllSelected;
  }, [paginatedConnections, selectedConnections, isAllSelected]);

  console.log('CTraderTokenManagement render:', {
    selectedConnectionsSize: selectedConnections.size,
    isAllSelected,
    isSomeSelected,
    paginatedConnectionsLength: paginatedConnections.length
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Total Connections</span>
            </div>
            <p className="text-2xl font-bold">{stats.total}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 bg-green-500 rounded-full" />
              <span className="text-sm font-medium">Connected</span>
            </div>
            <p className="text-2xl font-bold">{stats.connected}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 bg-orange-500 rounded-full" />
              <span className="text-sm font-medium">Expired</span>
            </div>
            <p className="text-2xl font-bold">{stats.expired}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 bg-red-500 rounded-full" />
              <span className="text-sm font-medium">Token Failed</span>
            </div>
            <p className="text-2xl font-bold">{stats.failed}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 bg-purple-500 rounded-full" />
              <span className="text-sm font-medium">Balance Failed</span>
            </div>
            <p className="text-2xl font-bold">{stats.balanceSyncFailed}</p>
          </CardContent>
        </Card>
      </div>

      {/* Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>cTrader Token & Balance Management</span>
            <div className="flex gap-2">
              {selectedConnections.size > 0 && (
                <>
                  <Badge variant="outline" className="px-3 py-1">
                    {selectedConnections.size} selected
                  </Badge>
                  <Button 
                    onClick={handleBatchBalanceSync}
                    disabled={isSyncingBalances}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <DollarSign className="h-4 w-4" />
                    Batch Sync
                  </Button>
                  <Button 
                    onClick={handleBatchRefresh}
                    disabled={isRefreshing}
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <Play className="h-4 w-4" />
                    Batch Refresh
                  </Button>
                </>
              )}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search and Filter */}
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by API name, client ID, or account name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Connections</SelectItem>
                <SelectItem value="connected">Connected</SelectItem>
                <SelectItem value="disconnected">Disconnected</SelectItem>
                <SelectItem value="expired">Expired Tokens</SelectItem>
                <SelectItem value="failed">Failed Refresh</SelectItem>
                <SelectItem value="success">Successful Refresh</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Pagination Info */}
          <div className="flex justify-between items-center text-sm text-muted-foreground">
            <span>
              Showing {((paginationConfig.page - 1) * paginationConfig.pageSize) + 1} to{' '}
              {Math.min(paginationConfig.page * paginationConfig.pageSize, paginationConfig.total)} of{' '}
              {paginationConfig.total} connections
            </span>
            <span>Page {paginationConfig.page} of {paginationConfig.totalPages}</span>
          </div>

          {/* Connections Table */}
          <div className="border rounded-lg">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-4 w-12">
                      <Checkbox
                        checked={isAllSelected ? true : isSomeSelected ? "indeterminate" : false}
                        onCheckedChange={handleSelectAll}
                      />
                    </th>
                    <th className="text-left p-4 font-medium">API Name</th>
                    <th className="text-left p-4 font-medium">Account</th>
                    <th className="text-left p-4 font-medium">Client ID</th>
                    <th className="text-left p-4 font-medium">Status</th>
                    <th className="text-left p-4 font-medium">Expires</th>
                    <th className="text-left p-4 font-medium">Last Refresh</th>
                    <th className="text-left p-4 font-medium">Balance Sync</th>
                    <th className="text-left p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedConnections.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center p-8 text-muted-foreground">
                        {searchQuery || statusFilter !== 'all' 
                          ? 'No connections match your filters'
                          : 'No cTrader connections found'
                        }
                      </td>
                    </tr>
                  ) : (
                    paginatedConnections.map((connection) => (
                      <tr key={connection.id} className="border-b hover:bg-muted/25">
                        <td className="p-4">
                          <Checkbox
                            checked={selectedConnections.has(connection.id)}
                            onCheckedChange={(checked) => handleSelectConnection(connection.id, checked)}
                          />
                        </td>
                        <td className="p-4">
                          <div className="font-medium">{connection.api_name}</div>
                        </td>
                        <td className="p-4">
                          <div className="text-sm text-muted-foreground">
                            {connection.csp_account_name || 'Unknown'}
                          </div>
                        </td>
                        <td className="p-4">
                          <code className="text-xs bg-muted px-2 py-1 rounded">
                            {connection.client_id.substring(0, 20)}...
                          </code>
                        </td>
                        <td className="p-4">
                          <div className="flex flex-col gap-1">
                            <Badge variant={getStatusBadgeVariant(connection.status)}>
                              {connection.status}
                            </Badge>
                            {connection.refresh_status && (
                              <Badge 
                                variant={getRefreshStatusBadgeVariant(connection.refresh_status)}
                                className="text-xs"
                              >
                                {connection.refresh_status}
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          {connection.expire_date ? (
                            <div className={`text-sm ${isTokenExpired(connection.expire_date) ? 'text-red-600 font-medium' : ''}`}>
                              {format(new Date(connection.expire_date), 'MMM d, yyyy HH:mm')}
                              {isTokenExpired(connection.expire_date) && (
                                <div className="text-xs text-red-500">Expired</div>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">No expiry</span>
                          )}
                        </td>
                        <td className="p-4">
                          {connection.last_refresh_attempt ? (
                            <div className="text-sm">
                              {format(new Date(connection.last_refresh_attempt), 'MMM d, HH:mm')}
                              {connection.refresh_error_message && (
                                <div className="text-xs text-red-500 mt-1">
                                  {connection.refresh_error_message.substring(0, 50)}...
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">Never</span>
                          )}
                        </td>
                        <td className="p-4">
                          <div className="flex flex-col gap-1">
                            {connection.balance_sync_status && (
                              <Badge 
                                variant={getBalanceSyncStatusBadgeVariant(connection.balance_sync_status)}
                                className="text-xs"
                              >
                                {connection.balance_sync_status}
                              </Badge>
                            )}
                            {connection.last_balance_sync ? (
                              <div className="text-xs text-muted-foreground">
                                {format(new Date(connection.last_balance_sync), 'MMM d, HH:mm')}
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">Never synced</span>
                            )}
                            {connection.balance_sync_error && (
                              <div className="text-xs text-red-500 mt-1">
                                {connection.balance_sync_error.substring(0, 30)}...
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => refreshSingleToken(connection.id)}
                              disabled={refreshingConnections.has(connection.id) || isRefreshing}
                              className="flex items-center gap-1"
                            >
                              <RefreshCw className={`h-3 w-3 ${refreshingConnections.has(connection.id) ? 'animate-spin' : ''}`} />
                              {refreshingConnections.has(connection.id) ? 'Refreshing' : 'Refresh'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => syncSingleBalance(connection.id)}
                              disabled={syncingBalanceConnections.has(connection.id) || isSyncingBalances}
                              className="flex items-center gap-1"
                            >
                              <DollarSign className={`h-3 w-3 ${syncingBalanceConnections.has(connection.id) ? 'animate-spin' : ''}`} />
                              {syncingBalanceConnections.has(connection.id) ? 'Syncing' : 'Sync'}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {paginationConfig.totalPages > 1 && (
            <div className="flex justify-center">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious 
                      onClick={goToPreviousPage}
                      className={!paginationConfig.page || paginationConfig.page <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                  
                  {[...Array(paginationConfig.totalPages)].map((_, index) => {
                    const page = index + 1;
                    const isCurrentPage = page === paginationConfig.page;
                    
                    // Show first, last, current, and adjacent pages
                    if (
                      page === 1 || 
                      page === paginationConfig.totalPages || 
                      Math.abs(page - paginationConfig.page) <= 1
                    ) {
                      return (
                        <PaginationItem key={page}>
                          <PaginationLink
                            onClick={() => goToPage(page)}
                            isActive={isCurrentPage}
                            className="cursor-pointer"
                          >
                            {page}
                          </PaginationLink>
                        </PaginationItem>
                      );
                    }
                    
                    // Show ellipsis
                    if (
                      page === paginationConfig.page - 2 || 
                      page === paginationConfig.page + 2
                    ) {
                      return (
                        <PaginationItem key={page}>
                          <PaginationEllipsis />
                        </PaginationItem>
                      );
                    }
                    
                    return null;
                  })}
                  
                  <PaginationItem>
                    <PaginationNext 
                      onClick={goToNextPage}
                      className={!paginationConfig.page || paginationConfig.page >= paginationConfig.totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Enhanced Batch Progress Dialog */}
      <EnhancedBatchProgressDialog
        isOpen={showBatchProgress}
        onClose={() => setShowBatchProgress(false)}
        jobId={batchJobId}
        jobType={batchJobType}
      />
    </div>
  );
}
